import React, { useEffect, useRef, useState } from 'react';
import { message, Modal, Input } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useProjectStore } from '../../hooks/useProjectStore';
import { warningAPI, MonitorRun } from '../../services/api';

// ═══════════════════════════════════════════════
// /warning/runs — 监测会话列表 (卡片视图)
// ═══════════════════════════════════════════════
const TH = {
  bg: '#FFFFFF', text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  accent: '#C2533A', accentLight: 'rgba(194,83,58,0.08)',
  ok: '#1E8449', warn: '#D4770C', critical: '#C0392B',
  headerBorder: '#D6DAE0', card: '#F9FAFB',
  fontMono: '"Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace',
};

const VERDICT_LABEL: Record<string, string> = { safe: '正常', warn: '预警', alarm: '报警' };
const VERDICT_COLOR: Record<string, string> = { safe: TH.ok, warn: TH.warn, alarm: TH.critical };
const SCENARIO_LABEL: Record<string, string> = {
  normal: '正常场景', screenout: '砂堵场景', frac_hit: '压窜场景',
};

export default function WarningRunsPage() {
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.id;
  const nav = useNavigate();

  const [runs, setRuns] = useState<MonitorRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [wellName, setWellName] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState('');
  const fileRef = useRef<HTMLInputElement | null>(null);

  // 从文件名提取段号: "…第5段…" → 5
  const parseStage = (fn: string) => {
    const m = fn.match(/第\s*(\d+)\s*段/);
    return m ? Number(m[1]) : 0;
  };
  // 从文件名提取井名: "阳102H3-1井…" → 阳102H3-1
  const parseWell = (fn: string) => {
    const m = fn.match(/^(.+?)井/);
    return m ? m[1] : '';
  };

  const onPickFiles = (fl: FileList | null) => {
    if (!fl || !fl.length) return;
    const arr = Array.from(fl);
    setFiles(arr);
    if (!wellName) setWellName(parseWell(arr[0].name));
  };

  const runImport = async () => {
    if (!projectId || !files.length) { message.warning('请选择文件'); return; }
    setImporting(true);
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const stage = parseStage(f.name);
      setProgress(`(${i + 1}/${files.length}) 第${stage}段 ${f.name} …`);
      const form = new FormData();
      form.append('file', f);
      form.append('project_id', String(projectId));
      form.append('well_name', wellName || parseWell(f.name));
      form.append('stage_number', String(stage));
      form.append('scenario', 'real');
      try {
        await warningAPI.importCurve(form);
        ok++;
      } catch (e: any) {
        const d = e?.response?.data;
        message.error(`${f.name}: ${typeof d === 'string' ? d : d?.detail || '导入失败'}`);
      }
    }
    setImporting(false);
    setProgress('');
    message.success(`导入完成: ${ok}/${files.length} 段`);
    setImportOpen(false);
    setFiles([]);
    await load();
  };

  const load = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await warningAPI.listRuns(projectId);
      setRuns(Array.isArray(data) ? data : (data as any)?.results ?? []);
    } catch (e: any) {
      message.error(e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [projectId]);

  const remove = (r: MonitorRun) => {
    Modal.confirm({
      title: `删除 #${r.id} (第 ${r.stage_number} 段) ?`,
      onOk: async () => {
        try {
          await warningAPI.deleteRun(r.id);
          message.success('已删除');
          await load();
        } catch { message.error('删除失败'); }
      },
    });
  };

  if (!projectId) return <div style={{ padding: 40, color: TH.textSec }}>请先选择项目</div>;

  // 汇总
  const stats = {
    total: runs.length,
    safe: runs.filter((r) => r.verdict === 'safe').length,
    warn: runs.filter((r) => r.verdict === 'warn').length,
    alarm: runs.filter((r) => r.verdict === 'alarm').length,
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: TH.bg, fontFamily: 'inherit' }}>
      <div style={{ padding: '8px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${TH.headerBorder}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: TH.text, margin: 0 }}>监测会话</h2>
          <span style={{ fontSize: 12, color: TH.textMuted }}>
            共 {stats.total} · 正常 <b style={{ color: TH.ok }}>{stats.safe}</b> ·
            预警 <b style={{ color: TH.warn }}>{stats.warn}</b> ·
            报警 <b style={{ color: TH.critical }}>{stats.alarm}</b>
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setImportOpen(true)} style={{
            padding: '5px 14px', borderRadius: 4, fontSize: 12, fontWeight: 600,
            background: '#fff', color: TH.ok, border: `1px solid ${TH.ok}`, cursor: 'pointer',
          }}>↑ 导入施工曲线</button>
          <button onClick={() => nav('/warning/live')} style={{
            padding: '5px 14px', borderRadius: 4, fontSize: 12, fontWeight: 600,
            background: TH.accent, color: '#fff', border: 'none', cursor: 'pointer',
          }}>+ 合成监测 → 实时分析</button>
        </div>
      </div>

      <Modal
        open={importOpen}
        title="导入真实压裂施工曲线 (秒点 Excel)"
        onCancel={() => !importing && setImportOpen(false)}
        onOk={runImport}
        okText={importing ? '导入中…' : '开始导入'}
        okButtonProps={{ disabled: importing || !files.length }}
        cancelButtonProps={{ disabled: importing }}
        width={560}
        destroyOnClose
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
          <div>
            <div style={{ fontSize: 12, color: TH.textSec, marginBottom: 4 }}>井名(留空则按文件名自动识别)</div>
            <Input value={wellName} onChange={(e) => setWellName(e.target.value)} placeholder="如 阳102H3-1" />
          </div>
          <div>
            <div style={{ fontSize: 12, color: TH.textSec, marginBottom: 4 }}>
              选择秒点 Excel(可多选,一文件=一段,段号自动从文件名"第X段"识别)
            </div>
            <input ref={fileRef} type="file" accept=".xlsx" multiple
              onChange={(e) => onPickFiles(e.target.files)} />
          </div>
          {files.length > 0 && (
            <div style={{ maxHeight: 180, overflow: 'auto', border: `1px solid ${TH.headerBorder}`, borderRadius: 4, padding: 8, fontSize: 12 }}>
              {files.map((f, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                  <span style={{ color: TH.text }}>{f.name}</span>
                  <span style={{ color: TH.textMuted, fontFamily: 'monospace' }}>第 {parseStage(f.name) || '?'} 段</span>
                </div>
              ))}
            </div>
          )}
          {importing && <div style={{ fontSize: 12, color: TH.accent }}>{progress}</div>}
          <div style={{ fontSize: 11, color: TH.textMuted, lineHeight: 1.6 }}>
            列名自动匹配:时间 / 井压力 / 施工排量 / 阶段液量 / 液量累计 / 砂浓度 / 阶段砂量 / 总砂量。
            导入后可在列表点开看曲线 + Nolte-Smith 分析。
          </div>
        </div>
      </Modal>

      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: TH.textMuted }}>加载中…</div>
        ) : runs.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: TH.textMuted, fontSize: 13 }}>
            <div style={{ fontSize: 30, opacity: 0.4, marginBottom: 12 }}>○</div>
            无监测会话 — 去「实时分析」选段生成
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
            {runs.map((r) => (
              <div key={r.id} style={{
                background: TH.card,
                border: `1px solid ${r.verdict === 'alarm' ? TH.critical : r.verdict === 'warn' ? TH.warn : TH.headerBorder}`,
                borderLeft: `4px solid ${VERDICT_COLOR[r.verdict] || TH.textMuted}`,
                borderRadius: 5, padding: 12,
                cursor: 'pointer',
              }} onClick={() => nav(`/warning/runs/${r.id}`)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: TH.text }}>
                    #{r.id} · 第 {r.stage_number} 段
                  </div>
                  <span style={{
                    padding: '2px 8px', fontSize: 10, fontWeight: 700, borderRadius: 10,
                    background: VERDICT_COLOR[r.verdict], color: '#fff',
                  }}>{VERDICT_LABEL[r.verdict]}</span>
                </div>
                <div style={{ fontSize: 11, color: TH.textSec, marginBottom: 4 }}>
                  {SCENARIO_LABEL[r.scenario]} · 方案: {r.design_name}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 11 }}>
                  {[
                    ['峰值压力', `${r.peak_pressure_mpa} MPa`],
                    ['平均排量', `${r.avg_rate_m3min} m³/min`],
                    ['最大砂比', `${r.max_conc_kgm3} kg/m³`],
                    ['事件数', r.event_count],
                  ].map(([l, v], i) => (
                    <div key={i}>
                      <span style={{ color: TH.textMuted }}>{l}: </span>
                      <span style={{ fontFamily: TH.fontMono, fontWeight: 600, color: TH.text }}>{v}</span>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 10, color: TH.textMuted, marginTop: 6, display: 'flex', justifyContent: 'space-between' }}>
                  <span>{new Date(r.created_at).toLocaleString('zh-CN')}</span>
                  <span style={{ cursor: 'pointer', color: TH.warn }} onClick={(e) => { e.stopPropagation(); remove(r); }}>
                    删除
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
